// biome-ignore-all lint/complexity/noStaticOnlyClass: legacy config pattern
// biome-ignore-all lint/suspicious/noDocumentCookie: cookie cleanup is required
/**
 * This file exports a `ClientConfig` class that provides configuration
 * options used by the client. These values are sourced from cookies if
 * present, otherwise from environment variables.
 *
 * The `ClientConfiguration` interface defines the properties that can be
 * set in the configuration object.
 *
 * The `getCookie` function is used internally to retrieve the config object
 * from a cookie, and then remove the cookie from the browser once it's been
 * read. This is done to prevent the cookie from being sent to the server.
 *
 * @remarks
 * - the cookie is populated from a server-side cookie in `server.ts`.
 */

interface ClientConfiguration {
  logoutUrl: string;
  loginUrl: string;
  downloadUrl: string;
  leasingApiUrl: string;
  subscriptionKey: string;
  apiUrl: string;
  applicationInsightsConnectionString: string;
}

export class ClientConfig {
  static logoutUrl: string;
  static loginUrl: string;
  static downloadUrl: string;
  static leasingApiUrl: string;
  static apiUrl: string;
  static subscriptionKey: string;
  static applicationInsightsConnectionString: string;

  static {
    const config = getCookie('clientConfig');
    const mockOrigin =
      import.meta.env.MODE === 'mock' ? window.location.origin : undefined;

    ClientConfig.logoutUrl = mockOrigin
      ? `${mockOrigin}/api/v1/authenticate/signout`
      : config?.logoutUrl ||
        (import.meta.env.VITE_LOGOUT_URL as string) ||
        'http://localhost:3000/logout';
    ClientConfig.loginUrl = mockOrigin
      ? `${mockOrigin}/api/v1/authenticate/signin`
      : config?.loginUrl ||
        (import.meta.env.VITE_LOGIN_URL as string) ||
        'http://localhost:3000/login';
    ClientConfig.downloadUrl = mockOrigin
      ? mockOrigin
      : config?.downloadUrl ||
        (import.meta.env.VITE_DOWNLOAD_URL as string) ||
        'http://localhost:3000/documents/download';
    ClientConfig.apiUrl = mockOrigin
      ? mockOrigin
      : config?.apiUrl ||
        (import.meta.env.VITE_API_URL as string) ||
        'http://localhost:3000';
    ClientConfig.leasingApiUrl = mockOrigin
      ? mockOrigin
      : config?.leasingApiUrl ||
        (import.meta.env.VITE_LEASING_API_URL as string) ||
        'http://localhost:3000';
    ClientConfig.subscriptionKey =
      config?.subscriptionKey ||
      (import.meta.env.VITE_SUBSCRIPTION_KEY as string) ||
      '';
    ClientConfig.applicationInsightsConnectionString =
      config?.applicationInsightsConnectionString ||
      (import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING as string) ||
      '';
  }
}

function getCookie(cookieName: string): ClientConfiguration | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie;

  const startPosition = cookies.search(`${cookieName}=`);

  if (startPosition === -1) {
    return null;
  }

  const endPosition = cookies.indexOf(';', startPosition);

  const cookie = decodeURIComponent(
    cookies
      .substring(startPosition, endPosition !== -1 ? endPosition : undefined)
      .split('=')
      .at(1) ?? '',
  );

  // wipe the cookie when its been read
  document.cookie =
    'clientConfig=; Path=/; Domain=.kommunekredit.dk; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=None';

  //document.cookie = 'config=;expires=Thu, 01 Jan 1970 00:00:00 GMT';

  return (
    cookie.charAt(0) === '{' ? JSON.parse(cookie) : cookie
  ) as ClientConfiguration;
}
